/**
 * CSS selector prefixing utility for sandboxing external stylesheets.
 *
 * Uses postcss AST-walking to prefix all CSS selectors with a container
 * selector, preventing styles from leaking outside the preview container.
 */
import postcss from 'postcss';

/** Dangerous CSS patterns to strip for security. */
const DANGEROUS_PATTERNS = [
  // @import - can load arbitrary external CSS
  /@import\s+[^;]+;/gi,
  // expression() - IE CSS expressions (JS execution)
  /expression\s*\([^)]*\)/gi,
  // javascript: in url()
  /url\s*\(\s*(['"]?)javascript:[^)]*\)/gi,
];

/** Pattern for non-https url() values (strip for security). */
const NON_HTTPS_URL = /url\s*\(\s*(['"]?)(?!https:\/\/|data:|['"]?#)([^)'"]+)\1\s*\)/gi;

/**
 * Sanitize CSS by removing dangerous patterns.
 *
 * Strips:
 * - @import statements (prevents loading arbitrary external CSS)
 * - expression() calls (prevents JS execution in IE)
 * - javascript: URLs
 * - Non-HTTPS url() values (except data: URIs and fragment refs)
 */
export function sanitizeCss(css: string): string {
  let result = css;

  for (const pattern of DANGEROUS_PATTERNS) {
    result = result.replace(pattern, '/* [stripped] */');
  }

  // Strip non-https url() values
  result = result.replace(NON_HTTPS_URL, 'url(/* [stripped] */)');

  return result;
}

/** At-rules whose child rules should NOT be prefixed. */
const PASSTHROUGH_ATRULES = new Set(['keyframes', 'font-face']);

/**
 * Check if a rule node is inside a passthrough at-rule (e.g. @keyframes).
 */
function isInsidePassthroughAtRule(node: postcss.ChildNode): boolean {
  let current: postcss.Container | postcss.Document | undefined = node.parent;
  while (current) {
    if (current.type === 'atrule' && PASSTHROUGH_ATRULES.has((current as postcss.AtRule).name)) {
      return true;
    }
    current = 'parent' in current ? (current as postcss.ChildNode).parent : undefined;
  }
  return false;
}

/**
 * Prefix all CSS selectors with a container selector for sandboxing.
 *
 * Handles:
 * - Simple selectors: `.btn { }` -> `.prefix .btn { }`
 * - Multiple selectors: `.a, .b { }` -> `.prefix .a, .prefix .b { }`
 * - @media queries: selectors inside are prefixed, @media rule passes through
 * - @keyframes: passes through completely (no prefixing of keyframe names or stops)
 * - @font-face: passes through completely
 *
 * @param css - Raw CSS string
 * @param prefix - Container selector to prepend (e.g., '.preview-container')
 * @returns Prefixed CSS string
 */
export function prefixCssSelectors(css: string, prefix: string): string {
  const sanitized = sanitizeCss(css);

  if (!sanitized.trim()) {
    return sanitized;
  }

  try {
    const root = postcss.parse(sanitized, { from: undefined });

    root.walk((node) => {
      if (node.type === 'rule') {
        const rule = node as postcss.Rule;

        // Don't prefix selectors inside @keyframes or @font-face
        if (isInsidePassthroughAtRule(rule)) {
          return;
        }

        rule.selectors = rule.selectors.map((sel) => `${prefix} ${sel}`);
      }
    });

    return root.toString();
  } catch {
    // Graceful degradation: return sanitized CSS without prefixing
    return sanitized;
  }
}
